import { LucideIcon, FolderPlus, MessageSquare, Bot, FileText, Upload, Share2, Settings, Calculator, Wand2, User, Briefcase, Plus, Edit, Check, Save, Sparkles, Lock, Users, Eye, CreditCard, FileSignature, Folder, MousePointer, PenTool, ArrowRight } from "lucide-react";

export interface GuideStep {
  order: number;
  title: string;
  description: string;
  icon: LucideIcon;
  tip?: string;
  warning?: string;
}

export interface Guide {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: "iniciante" | "intermediário" | "avançado";
  duration: string;
  icon: LucideIcon;
  steps: GuideStep[];
  relatedGuides?: string[];
}

export const DOCS_GUIDES: Guide[] = [
  {
    id: "criar-primeiro-caso",
    title: "Criando seu Primeiro Caso",
    description: "Aprenda do zero como criar e configurar seu primeiro caso no Justiçai.",
    category: "casos",
    difficulty: "iniciante",
    duration: "5 min",
    icon: FolderPlus,
    steps: [
      { order: 1, title: "Acesse a página de Casos", description: "Na sidebar à esquerda da tela, clique em 'Casos / Clientes'. Esta é a área onde todos os seus processos ficam organizados.", icon: Briefcase },
      { order: 2, title: "Clique em Novo Caso", description: "No canto superior direito da tela, você verá um botão 'Novo Caso'. Clique nele para abrir o formulário de criação.", icon: Plus },
      { order: 3, title: "Preencha o nome do caso", description: "Digite um nome descritivo para identificar o caso. Exemplo: 'João Silva - Reclamatória Trabalhista' ou 'Empresa XYZ - Contrato Comercial'.", icon: Edit, tip: "Use nomes claros que ajudem a encontrar o caso rapidamente depois." },
      { order: 4, title: "Adicione uma descrição (opcional)", description: "Você pode adicionar detalhes extras como número do processo, vara, comarca ou observações importantes.", icon: FileText },
      { order: 5, title: "Personalize a aparência (opcional)", description: "Escolha um ícone e uma cor para o caso. Isso ajuda na identificação visual rápida.", icon: Eye },
      { order: 6, title: "Clique em Criar", description: "Pronto! Seu caso foi criado. Agora você pode adicionar documentos, conversar com a IA e gerar peças processuais.", icon: Check },
    ],
    relatedGuides: ["upload-base-conhecimento", "gerar-documento-ia"],
  },
  {
    id: "upload-base-conhecimento",
    title: "Fazendo Upload para a Base de Conhecimento",
    description: "Aprenda a enviar documentos para que a IA possa analisar e usar nas respostas.",
    category: "casos",
    difficulty: "iniciante",
    duration: "3 min",
    icon: Upload,
    steps: [
      { order: 1, title: "Abra o caso desejado", description: "Clique no caso para o qual deseja adicionar documentos. A página do caso será aberta.", icon: Briefcase },
      { order: 2, title: "Vá para Base de Conhecimento", description: "Dentro do caso, localize a aba 'Base de Conhecimento' ou 'Fontes'. Clique nela.", icon: Folder },
      { order: 3, title: "Clique em Upload ou arraste arquivos", description: "Clique no botão 'Upload' ou simplesmente arraste os arquivos para a área indicada.", icon: Upload, tip: "Você pode enviar vários arquivos de uma vez." },
      { order: 4, title: "Aguarde o processamento", description: "A IA irá ler e indexar o conteúdo dos documentos. Isso pode levar alguns segundos dependendo do tamanho.", icon: Sparkles },
      { order: 5, title: "Confirme o upload", description: "Quando o ícone mudar para verde ou aparecer 'Processado', o documento está pronto para uso.", icon: Check },
    ],
    relatedGuides: ["usar-chat-caso"],
  },
  {
    id: "gerar-documento-ia",
    title: "Gerando um Documento com IA",
    description: "Passo a passo para criar petições, contratos e outros documentos usando inteligência artificial.",
    category: "casos",
    difficulty: "iniciante",
    duration: "5 min",
    icon: FileText,
    steps: [
      { order: 1, title: "Abra o caso", description: "Acesse o caso para o qual deseja gerar um documento.", icon: Briefcase },
      { order: 2, title: "Vá para a área de Documentos", description: "Clique na aba 'Documentos' ou no botão 'Criar Documento'.", icon: FileText },
      { order: 3, title: "Descreva o documento desejado", description: "No campo de texto, escreva qual documento você precisa. Seja específico. Exemplo: 'Petição inicial de ação de indenização por danos morais contra a empresa XYZ por atraso na entrega de produto'.", icon: PenTool, tip: "Quanto mais detalhes você fornecer, melhor será o documento gerado." },
      { order: 4, title: "Clique em Gerar", description: "Clique no botão 'Gerar Documento' e aguarde. A IA irá criar o documento com base nas informações do caso e na sua solicitação.", icon: Sparkles },
      { order: 5, title: "Revise e edite", description: "O documento aparecerá no editor. Leia atentamente, faça os ajustes necessários e personalize conforme o caso específico.", icon: Edit, warning: "Sempre revise os documentos gerados pela IA antes de usar. É responsabilidade do advogado garantir a precisão." },
      { order: 6, title: "Salve ou exporte", description: "Clique em 'Salvar' para guardar no sistema, ou em 'Baixar PDF' para exportar o documento.", icon: Save },
    ],
    relatedGuides: ["configurar-papel-timbrado"],
  },
  {
    id: "usar-chat-caso",
    title: "Usando o Chat do Caso",
    description: "Aprenda a conversar com a IA sobre um caso específico, usando os documentos da base de conhecimento.",
    category: "casos",
    difficulty: "iniciante",
    duration: "3 min",
    icon: MessageSquare,
    steps: [
      { order: 1, title: "Abra o caso", description: "Clique no caso sobre o qual deseja conversar com a IA.", icon: Briefcase },
      { order: 2, title: "Acesse o Chat", description: "Dentro do caso, clique na aba 'Chat' ou no ícone de conversa.", icon: MessageSquare },
      { order: 3, title: "Digite sua pergunta", description: "Escreva sua pergunta no campo de texto. A IA tem acesso a todos os documentos que você enviou para a base de conhecimento.", icon: PenTool, tip: "Pergunte sobre prazos, partes, teses possíveis, resumos de documentos, etc." },
      { order: 4, title: "Receba a resposta", description: "A IA irá responder com base nos documentos do caso e no conhecimento jurídico geral.", icon: Sparkles },
      { order: 5, title: "Continue a conversa", description: "Você pode fazer perguntas de acompanhamento. A IA lembra do contexto da conversa.", icon: ArrowRight },
    ],
  },
  {
    id: "configurar-papel-timbrado",
    title: "Configurando seu Papel Timbrado",
    description: "Personalize o cabeçalho e rodapé que aparecem em todos os documentos gerados.",
    category: "configuracoes",
    difficulty: "iniciante",
    duration: "5 min",
    icon: FileSignature,
    steps: [
      { order: 1, title: "Acesse Configurações", description: "Na sidebar, clique em 'Configurações'.", icon: Settings },
      { order: 2, title: "Vá em Papel Timbrado", description: "Clique na aba 'Papel Timbrado' ou 'Documentos'.", icon: FileSignature },
      { order: 3, title: "Adicione a logo", description: "Clique em 'Enviar Logo' e selecione a imagem do logo do seu escritório. Recomendamos usar PNG ou JPG.", icon: Upload, tip: "Use uma imagem de boa qualidade para melhor resultado na impressão." },
      { order: 4, title: "Configure o cabeçalho", description: "Preencha: nome do escritório, endereço, telefone, email. Essas informações aparecerão no topo dos documentos.", icon: Edit },
      { order: 5, title: "Configure o rodapé", description: "Adicione: número da OAB, seccional, informações adicionais de contato.", icon: Edit },
      { order: 6, title: "Visualize e salve", description: "Use a pré-visualização para ver como ficará. Quando estiver satisfeito, clique em 'Salvar'.", icon: Check },
    ],
  },
  {
    id: "usar-chat-juridico",
    title: "Usando o Chat Jurídico",
    description: "Aprenda a usar o chat com a IA Laib 3.7 para tirar dúvidas jurídicas gerais.",
    category: "chat",
    difficulty: "iniciante",
    duration: "3 min",
    icon: MessageSquare,
    steps: [
      { order: 1, title: "Acesse o Chat Jurídico", description: "Na sidebar, clique em 'Chat Jurídico'.", icon: MessageSquare },
      { order: 2, title: "Inicie uma nova conversa", description: "Clique em 'Nova Conversa' ou no botão '+' para começar.", icon: Plus },
      { order: 3, title: "Faça sua pergunta", description: "Digite sua dúvida jurídica. Pode ser sobre qualquer área do Direito Brasileiro: trabalhista, civil, penal, tributário, etc.", icon: PenTool, tip: "A IA cita legislação, jurisprudência e doutrina quando relevante." },
      { order: 4, title: "Leia a resposta", description: "A IA irá responder sua pergunta com embasamento legal. Você pode copiar a resposta ou continuar a conversa.", icon: Sparkles },
      { order: 5, title: "Salve conversas importantes", description: "Todas as conversas são salvas automaticamente. Você pode renomeá-las para facilitar a organização.", icon: Save },
    ],
  },
  {
    id: "configurar-assessor",
    title: "Configurando o Assessor",
    description: "Personalize seu assistente virtual para que ele conheça seus casos e responda do seu jeito.",
    category: "assessor",
    difficulty: "intermediário",
    duration: "5 min",
    icon: Bot,
    steps: [
      { order: 1, title: "Acesse as configurações do Assessor", description: "Vá em Configurações > Assessor, ou clique no ícone de engrenagem na página do Assessor.", icon: Settings },
      { order: 2, title: "Ative o Assessor", description: "Marque a opção 'Ativar Assessor' para habilitar o assistente.", icon: Check },
      { order: 3, title: "Personalize nome e avatar", description: "Escolha um nome para seu assistente e selecione um avatar ou ícone.", icon: User },
      { order: 4, title: "Defina o escopo de acesso", description: "Escolha se o assessor pode ver todos os casos, apenas alguns selecionados, ou nenhum.", icon: Lock, tip: "Você pode excluir casos confidenciais do acesso do assessor." },
      { order: 5, title: "Escolha o tom de resposta", description: "Selecione como o assessor deve se comunicar: formal, profissional, amigável ou direto.", icon: MessageSquare },
      { order: 6, title: "Salve as configurações", description: "Clique em 'Salvar'. Seu assessor está pronto para uso!", icon: Save },
    ],
  },
  {
    id: "compartilhar-caso-colegas",
    title: "Compartilhando um Caso com Colegas",
    description: "Aprenda a compartilhar casos com outros advogados do seu escritório ou parceiros.",
    category: "compartilhamento",
    difficulty: "intermediário",
    duration: "3 min",
    icon: Share2,
    steps: [
      { order: 1, title: "Abra o caso", description: "Acesse o caso que deseja compartilhar.", icon: Briefcase },
      { order: 2, title: "Clique em Compartilhar", description: "No canto superior direito, clique no botão 'Compartilhar' (ícone de compartilhamento).", icon: Share2 },
      { order: 3, title: "Adicione a pessoa", description: "Digite o email do colega ou selecione da sua lista de contatos.", icon: Users },
      { order: 4, title: "Defina o nível de acesso", description: "Escolha a permissão: 'Visualizar' (só ver), 'Editar' (modificar documentos) ou 'Acesso Total' (tudo, inclusive compartilhar).", icon: Lock, warning: "Cuidado com 'Acesso Total'. A pessoa poderá compartilhar o caso com terceiros." },
      { order: 5, title: "Envie o convite", description: "Clique em 'Compartilhar'. A pessoa receberá um email com o link de acesso.", icon: Check },
    ],
  },
  {
    id: "comprar-creditos",
    title: "Comprando Créditos Extras",
    description: "Saiba como adquirir mais créditos de IA quando seu saldo estiver baixo.",
    category: "configuracoes",
    difficulty: "iniciante",
    duration: "2 min",
    icon: CreditCard,
    steps: [
      { order: 1, title: "Acesse Configurações", description: "Na sidebar, clique em 'Configurações'.", icon: Settings },
      { order: 2, title: "Vá em Créditos", description: "Clique na aba 'Créditos' ou 'Comprar Créditos'.", icon: CreditCard },
      { order: 3, title: "Veja seu saldo atual", description: "Visualize quantos créditos você ainda tem e quanto já usou.", icon: Eye },
      { order: 4, title: "Escolha um pacote", description: "Selecione o pacote de créditos que deseja comprar. Pacotes maiores geralmente têm melhor custo-benefício.", icon: MousePointer },
      { order: 5, title: "Finalize a compra", description: "Complete o pagamento. Os créditos serão adicionados imediatamente à sua conta.", icon: Check, tip: "O pagamento é processado de forma segura via Stripe." },
    ],
  },
  {
    id: "usar-calculadoras",
    title: "Usando as Calculadoras Jurídicas",
    description: "Aprenda a usar as calculadoras para cálculos trabalhistas, INSS, pensão e mais.",
    category: "calculos",
    difficulty: "iniciante",
    duration: "3 min",
    icon: Calculator,
    steps: [
      { order: 1, title: "Acesse Cálculos Jurídicos", description: "Na sidebar, clique em 'Cálculos Jurídicos'.", icon: Calculator },
      { order: 2, title: "Escolha a categoria", description: "Selecione o tipo de cálculo: Trabalhistas, INSS, Pensão, Aluguel, etc.", icon: MousePointer },
      { order: 3, title: "Selecione o cálculo específico", description: "Dentro da categoria, escolha qual cálculo precisa fazer. Exemplo: Rescisão, Férias, Horas Extras.", icon: MousePointer },
      { order: 4, title: "Preencha os dados", description: "Informe os valores solicitados: salário, datas, tipo de contrato, etc.", icon: Edit },
      { order: 5, title: "Clique em Calcular", description: "Clique no botão 'Calcular' para ver o resultado detalhado.", icon: Check },
      { order: 6, title: "Exporte ou copie", description: "Você pode copiar os valores ou exportar o cálculo completo.", icon: Save },
    ],
  },
  {
    id: "melhorar-texto",
    title: "Melhorando seu Texto Jurídico",
    description: "Use a IA para aprimorar a gramática, clareza e argumentação dos seus textos.",
    category: "melhore-texto",
    difficulty: "iniciante",
    duration: "2 min",
    icon: Wand2,
    steps: [
      { order: 1, title: "Acesse a ferramenta", description: "Na sidebar, clique em 'Melhore seu Texto'.", icon: Wand2 },
      { order: 2, title: "Cole seu texto", description: "No campo grande, cole o texto jurídico que deseja melhorar.", icon: PenTool },
      { order: 3, title: "Escolha o tipo de melhoria", description: "Selecione o que deseja: revisar gramática, melhorar argumentação, simplificar, formalizar, resumir ou expandir.", icon: Sparkles },
      { order: 4, title: "Clique em Melhorar", description: "Clique no botão 'Melhorar Texto' e aguarde a IA processar.", icon: Check },
      { order: 5, title: "Compare e copie", description: "Veja o texto melhorado ao lado do original. Copie o resultado clicando no botão 'Copiar'.", icon: Save },
    ],
  },
];

export const getGuideById = (id: string): Guide | undefined => {
  return DOCS_GUIDES.find(g => g.id === id);
};

export const getGuidesByCategory = (category: string): Guide[] => {
  return DOCS_GUIDES.filter(g => g.category === category);
};

export const getGuidesByDifficulty = (difficulty: Guide["difficulty"]): Guide[] => {
  return DOCS_GUIDES.filter(g => g.difficulty === difficulty);
};
